import type { Position, Direction, Ship, PlayerBoard, BSPlayer, BattleshipGame, AttackResult } from "./types.js";
import { SHIPS, GRID_SIZE } from "./types.js";

export function createEmptyBoard(): PlayerBoard {
  const grid: ("empty" | "ship")[][] = Array.from({ length: GRID_SIZE }, () =>
    Array.from({ length: GRID_SIZE }, () => "empty" as const)
  );
  const attackGrid: AttackResult[][] = Array.from({ length: GRID_SIZE }, () =>
    Array.from({ length: GRID_SIZE }, () => "unknown" as const)
  );
  return { grid, attackGrid, ships: [] };
}

export function canPlaceShip(board: PlayerBoard, shipSize: number, pos: Position, dir: Direction): boolean {
  const { row, col } = pos;
  for (let i = 0; i < shipSize; i++) {
    const r = dir === "v" ? row + i : row;
    const c = dir === "h" ? col + i : col;
    if (r < 0 || r >= GRID_SIZE || c < 0 || c >= GRID_SIZE) return false;
    if (board.grid[r][c] !== "empty") return false;
  }
  return true;
}

export function placeShip(board: PlayerBoard, shipDef: { name: string; size: number }, pos: Position, dir: Direction): PlayerBoard {
  const grid = board.grid.map((row) => [...row]);
  const positions: Position[] = [];
  for (let i = 0; i < shipDef.size; i++) {
    const r = dir === "v" ? pos.row + i : pos.row;
    const c = dir === "h" ? pos.col + i : pos.col;
    grid[r][c] = "ship";
    positions.push({ row: r, col: c });
  }
  const ship: Ship = { name: shipDef.name, size: shipDef.size, positions, hits: Array.from({ length: shipDef.size }, () => false) };
  return { ...board, grid, ships: [...board.ships, ship] };
}

export function attack(board: PlayerBoard, pos: Position): { result: AttackResult; sunk: Ship | null; board: PlayerBoard } {
  const attackGrid = board.attackGrid.map((row) => [...row]);
  const ships = board.ships.map((s) => ({ ...s, hits: [...s.hits], positions: [...s.positions] }));

  if (attackGrid[pos.row][pos.col] !== "unknown") {
    return { result: "miss", sunk: null, board: { ...board, attackGrid, ships } };
  }

  // Check if any ship occupies this position
  for (const ship of ships) {
    for (let i = 0; i < ship.positions.length; i++) {
      if (ship.positions[i].row === pos.row && ship.positions[i].col === pos.col) {
        ship.hits[i] = true;
        attackGrid[pos.row][pos.col] = "hit";
        const allHit = ship.hits.every((h) => h);
        return {
          result: "hit",
          sunk: allHit ? ship : null,
          board: { ...board, attackGrid, ships },
        };
      }
    }
  }

  attackGrid[pos.row][pos.col] = "miss";
  return { result: "miss", sunk: null, board: { ...board, attackGrid, ships } };
}

export function allSunk(board: PlayerBoard): boolean {
  return board.ships.every((s) => s.hits.every((h) => h));
}

export function createGame(player1CPU: boolean, player2CPU: boolean): BattleshipGame {
  return {
    phase: "placement",
    players: [
      { name: "Player 1", board: createEmptyBoard(), isCPU: player1CPU },
      { name: "Player 2", board: createEmptyBoard(), isCPU: player2CPU },
    ],
    turn: 0,
    winner: null,
    placementShip: 0,
  };
}

// Random ship placement for CPU
export function autoPlaceShips(board: PlayerBoard): PlayerBoard {
  let b = { ...board };
  for (const ship of SHIPS) {
    let placed = false;
    for (let attempts = 0; attempts < 200 && !placed; attempts++) {
      const dir: Direction = Math.random() < 0.5 ? "h" : "v";
      const row = Math.floor(Math.random() * GRID_SIZE);
      const col = Math.floor(Math.random() * GRID_SIZE);
      if (canPlaceShip(b, ship.size, { row, col }, dir)) {
        b = placeShip(b, ship, { row, col }, dir);
        placed = true;
      }
    }
    if (!placed) return autoPlaceShips(board); // retry
  }
  return b;
}

// CPU attack: random with adjacent-hunting after a hit
let cpuMemory: { player: 0 | 1; lastHit: Position | null; candidates: Position[] } = { player: 0, lastHit: null, candidates: [] };

export function cpuAttack(board: PlayerBoard, playerIdx: 0 | 1): Position {
  // Reset memory if needed
  if (cpuMemory.player !== playerIdx || cpuMemory.lastHit === null) {
    cpuMemory = { player: playerIdx, lastHit: null, candidates: [] };
  }

  // If we have a recent hit and adjacent candidates, try those first
  if (cpuMemory.lastHit && cpuMemory.candidates.length > 0) {
    const pick = cpuMemory.candidates.pop()!;
    if (board.attackGrid[pick.row][pick.col] === "unknown") return pick;
  }

  // Generate all unknown cells
  const unknowns: Position[] = [];
  for (let r = 0; r < GRID_SIZE; r++) {
    for (let c = 0; c < GRID_SIZE; c++) {
      if (board.attackGrid[r][c] === "unknown") unknowns.push({ row: r, col: c });
    }
  }

  // Random pick
  const pick = unknowns[Math.floor(Math.random() * unknowns.length)];

  // If hit, generate adjacent candidates for next turn
  cpuMemory.lastHit = null;
  cpuMemory.candidates = [];
  // (We'll set lastHit after the attack result comes back)

  return pick;
}

export function notifyCpuHit(pos: Position, playerIdx: 0 | 1) {
  cpuMemory.player = playerIdx;
  cpuMemory.lastHit = pos;
  cpuMemory.candidates = [
    { row: pos.row - 1, col: pos.col },
    { row: pos.row + 1, col: pos.col },
    { row: pos.row, col: pos.col - 1 },
    { row: pos.row, col: pos.col + 1 },
  ].filter((p) => p.row >= 0 && p.row < GRID_SIZE && p.col >= 0 && p.col < GRID_SIZE);
}
